import clsx from 'clsx';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';

import {
  formatTechnologyProficiencyLabel,
  type TechnologyProficiencyEntry,
} from '@/features/Collaborators/utils/resourcePresentation';

interface CapabilityBadgeProps {
  capability: Omit<TechnologyProficiencyEntry, 'key'> | TechnologyProficiencyEntry;
  size?: 'sm' | 'md';
  showPrimary?: boolean;
  className?: string;
}

const getSeniorityTone = (seniorityName?: string | null): string => {
  if (!seniorityName) {
    return 'border-slate-200 bg-slate-50 text-slate-600';
  }

  const normalized = seniorityName.toLowerCase();

  if (normalized.includes('estagi') || normalized.includes('trainee')) {
    return 'border-emerald-100 bg-emerald-50 text-emerald-700';
  }

  if (normalized.includes('junior') || normalized.includes('júnior') || normalized.includes('jr')) {
    return 'border-lime-100 bg-lime-50 text-lime-700';
  }

  if (normalized.includes('pleno') || normalized.includes('mid')) {
    return 'border-sky-100 bg-sky-50 text-sky-700';
  }

  if (normalized.includes('senior') || normalized.includes('sênior') || normalized.includes('sr')) {
    return 'border-indigo-100 bg-indigo-50 text-indigo-700';
  }

  if (normalized.includes('especialista') || normalized.includes('specialist')) {
    return 'border-rose-100 bg-rose-50 text-rose-700';
  }

  if (normalized.includes('lead')) {
    return 'border-amber-100 bg-amber-50 text-amber-700';
  }

  return 'border-slate-200 bg-slate-50 text-slate-600';
};

export function CapabilityBadge({
  capability,
  size = 'md',
  showPrimary = false,
  className,
}: CapabilityBadgeProps) {
  const title = formatTechnologyProficiencyLabel(capability);
  const isCompact = size === 'sm';

  return (
    <span
      title={title}
      className={clsx(
        'inline-flex items-stretch rounded-full border bg-white shadow-sm',
        showPrimary && capability.isPrimary ? 'border-amber-200' : 'border-slate-200',
        className,
      )}
    >
      {showPrimary && capability.isPrimary ? (
        <span
          className={clsx(
            'inline-flex items-center justify-center border-r border-amber-100 bg-amber-50 text-amber-600',
            isCompact ? 'px-1.5' : 'px-2',
          )}
        >
          <StarSolidIcon className={isCompact ? 'h-3 w-3' : 'h-3.5 w-3.5'} aria-hidden="true" />
          <span className="sr-only">Principal</span>
        </span>
      ) : null}

      <span
        className={clsx(
          'font-semibold text-slate-700 whitespace-nowrap',
          isCompact ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs',
        )}
      >
        {capability.technologyName}
      </span>

      {capability.seniorityName ? (
        <span
          className={clsx(
            'border-l font-medium',
            getSeniorityTone(capability.seniorityName),
            isCompact ? 'px-2 py-1 text-[11px]' : 'px-3 py-1.5 text-xs',
          )}
        >
          {capability.seniorityName}
        </span>
      ) : null}
    </span>
  );
}

export default CapabilityBadge;
